import { KeyIndicatorColor, useKeyIndicatorTags } from "react-migration/lib/map/useKeyIndicatorTags";
import { SkeletonLoading } from "react-migration/components/SkeletonLoading";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { useSelectionSidebarLoading } from "../../../Bundle/useSelectionSidebarLoadingState";
import { LayerTypeLayerSummaryProps, SupportedRegions } from "../../../types";
import { Insight } from "react-migration/components/Insight/Insight";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import Feature from "src/js/stores/user/Feature";
import { useSummaryPriceData } from "react-migration/domains/ownership/components/PropertySaleValue/PropertySaleValue";
import { useTransactionsAtSelection } from "../useTransactionsAtSelection";
import { getDefaultFilters } from "react-migration/domains/comparables/util/defaultFilters";
import { formattedDateRange, subtractTime } from "src/js/util/dates";
import { formatToCurrency } from "react-migration/lib/util/numberFormat";
import { dateString } from "src/js/util/dates";
import { useSnapshot } from "valtio";
import { userStore } from "src/js/stores/user/store";

export function ComparablesLayerSummary({ selection, onLoadingChange }: LayerTypeLayerSummaryProps) {
  const { t } = useTranslation();
  const { locale } = useSnapshot(userStore);
  const { salesData, loading, error } = useSummaryPriceData({ uprn: selection.id || "", skip: !selection.id });

  const {
    includedResults: rawSelectionResults,
    loading: selectionLoading,
    error: selectionError,
  } = useTransactionsAtSelection(selection, undefined, {
    ...getDefaultFilters(),
    dateFrom: formattedDateRange(subtractTime(new Date(), "years", 35)),
  });

  const selectionSalesData = Array.isArray(rawSelectionResults)
    ? [...rawSelectionResults]
        .sort((a, b) => (Date.parse(b.date_of_transfer) || 0) - (Date.parse(a.date_of_transfer) || 0))
        .map((transaction) => ({
          price: formatToCurrency({
            amount: transaction.price,
            currency: transaction.currency,
            locale,
          }),
          date: transaction.date_of_transfer ? dateString(transaction.date_of_transfer, locale) : "-",
        }))
    : rawSelectionResults;

  const latestSale = salesData[0] || selectionSalesData[0];

  useSelectionSidebarLoading(onLoadingChange, loading || selectionLoading);

  useKeyIndicatorTags(() => {
    return [
      {
        color: KeyIndicatorColor.DEFAULT,
        tag: latestSale?.price || t("workbench.summary.comparables.last_sold.empty"),
      },
    ];
  }, [latestSale?.price, t]);

  ComparablesLayerSummary.icon = hasFeature(Feature.usAccess)
    ? "icon-lt-refund-2-line"
    : "icon-lt-money-pound-circle-line";

  if (error && selectionError) {
    // caught by error boundary
    throw error || selectionError;
  }

  if (loading || selectionLoading) {
    return (
      <div className="relative p-2 h-[168px]">
        <SkeletonLoading rows={2} showPadding={false} bgClassName="bg-transparent" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2">
      <Insight.Wrapper title={t("workbench.summary.comparables.last_sold.title")}>
        <Insight.Body>{latestSale?.price || t("workbench.summary.comparables.last_sold.empty")}</Insight.Body>
      </Insight.Wrapper>
      <Insight.Wrapper title={t("workbench.summary.comparables.last_sold.description")}>
        <Insight.Body>{latestSale?.date || "-"}</Insight.Body>
      </Insight.Wrapper>
    </div>
  );
}
// @ts-expect-error icon defined in function
ComparablesLayerSummary.icon = ComparablesLayerSummary.icon as string;
ComparablesLayerSummary.supportedRegions = [SupportedRegions.england, SupportedRegions.us];
